import { EmptyState } from "@/components/EmptyState";
import { usePayload } from "@/lib/usePayload";

import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/cn";

function normalizeGoalSource(source: string): string {
  const value = (source || "").trim().toLowerCase();
  if (["direct", "chat", "phone inquiry", "direct / none", "direct/ none", "referral"].includes(value)) return "Direct/ none";
  if (value === "organic search") return "Organic Search";
  if (value === "organic") return "GTM Initiatives (Organic)";
  if (["paid search", "google - paid search"].includes(value)) return "Google - Paid Search";
  if (["paid social", "facebook", "hellosprout", "facebook/ig"].includes(value)) return "Facebook/IG";
  if (
    [
      "gtm organic",
      "gtm initiatives (organic)",
      "lead magnets",
      "ai website builder email entry",
      "digital menu web sign-up",
      "chatgpt.com / ai-assistant",
      "ai website builder sign-up",
    ].includes(value)
  ) {
    return "GTM Initiatives (Organic)";
  }
  if (["clover direct opt-in", "pos - direct (opt-in)"].includes(value)) return "Clover Direct Opt-in";
  return "Other";
}

const GOAL_SOURCE_ORDER = [
  "Google - Paid Search",
  "Facebook/IG",
  "Direct/ none",
  "Organic Search",
  "Clover Direct Opt-in",
  "GTM Initiatives (Organic)",
];

const FIXED_GOALS: Record<string, number> = {
  "Google - Paid Search": 50,
  "Facebook/IG": 130,
  "Direct/ none": 20,
  "Organic Search": 15,
  "Clover Direct Opt-in": 5,
  "GTM Initiatives (Organic)": 0,
};

const SEARCH_BUDGET = 5000;
const SOCIAL_BUDGET = 5000;

export function GoalTracking() {
  const { payload, isLoading: payloadLoading } = usePayload();

  if (!payload) return <EmptyState title="No data yet for this period" loading={payloadLoading} />;

  // Current Month Actuals
  const currentActuals: Record<string, number> = {};
  GOAL_SOURCE_ORDER.forEach((s) => (currentActuals[s] = 0));
  
  (payload.source_summary || []).forEach((s: any) => {
    const norm = normalizeGoalSource(s.source);
    if (GOAL_SOURCE_ORDER.includes(norm)) {
      currentActuals[norm] += s.mql || 0;
    }
  });

  const currentPaidSearchSpend = payload.paid_search_efficiency?.google_spend || payload.google_ads_summary?.total_spend || 0;
  const currentPaidSocialSpend = payload.paid_social_efficiency?.spend ?? payload.paid_social_efficiency?.meta_spend ?? payload.meta_summary?.total_spend ?? 0;

  const goals = FIXED_GOALS;
  let totalGoal = 0;
  let totalActual = 0;
  
  GOAL_SOURCE_ORDER.forEach((s) => {
    totalGoal += goals[s] || 0;
    totalActual += currentActuals[s] || 0;
  });

  const searchBudget = SEARCH_BUDGET;
  const socialBudget = SOCIAL_BUDGET;

  const formatThreshold = (actual: number, goal: number | null) => {
    if (!goal || goal === 0) return "—";
    const threshold = (actual - goal) / goal;
    const isPositive = threshold > 0;
    const isNegative = threshold < 0;
    return (
      <span className={cn(isPositive ? "text-green-600" : isNegative ? "text-red-600" : "text-slate-600")}>
        {threshold > 0 ? "+" : ""}{Math.round(threshold * 100)}%
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Goal Tracking</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          MQL pacing vs monthly goals — {payload.month}
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Goals and budgets are fixed for the current period.
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-100 text-left border-b border-slate-200">
                <th className="px-5 py-3 font-semibold text-slate-700">Channel / Source</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right">Monthly Goal</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right bg-blue-50">Actual</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right bg-yellow-50">Threshold from Goal</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right">Allocated Budget for Month</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right bg-blue-50">Actual Ad Spend</th>
                <th className="px-4 py-3 font-semibold text-slate-700 text-right bg-yellow-50">Threshold from Budget</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {/* Summary Row */}
              <tr className="bg-emerald-100/60 font-semibold border-b-2 border-slate-200 text-emerald-900">
                <td className="px-5 py-3">Summary</td>
                <td className="px-4 py-3 text-right numeric-value">{totalGoal}</td>
                <td className="px-4 py-3 text-right numeric-value bg-blue-50/50">{totalActual}</td>
                <td className="px-4 py-3 text-right numeric-value bg-yellow-50/50">
                  {formatThreshold(totalActual, totalGoal)}
                </td>
                <td className="px-4 py-3 text-right numeric-value">
                  {formatCurrency(searchBudget + socialBudget)}
                </td>
                <td className="px-4 py-3 text-right numeric-value bg-blue-50/50">
                  {formatCurrency(currentPaidSearchSpend + currentPaidSocialSpend)}
                </td>
                <td className="px-4 py-3 text-right numeric-value bg-yellow-50/50">
                  {formatThreshold(
                    currentPaidSearchSpend + currentPaidSocialSpend,
                    searchBudget + socialBudget
                  )}
                </td>
              </tr>

              {/* Source Rows */}
              {GOAL_SOURCE_ORDER.map((source) => {
                const isPaidSearch = source === "Google - Paid Search";
                const isPaidSocial = source === "Facebook/IG";
                const goal = goals[source];
                const actual = currentActuals[source];
                
                let allocatedBudget: number | null = null;
                let actualSpend: number | null = null;
 
                if (isPaidSearch) {
                  allocatedBudget = searchBudget;
                  actualSpend = currentPaidSearchSpend;
                } else if (isPaidSocial) {
                  allocatedBudget = socialBudget;
                  actualSpend = currentPaidSocialSpend;
                }

                return (
                  <tr key={source} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 font-medium text-slate-700">{source}</td>
                    <td className="px-4 py-3 text-right numeric-value text-slate-600">
                      {goal}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value text-slate-600 bg-blue-50/50">
                      {actual}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value bg-yellow-50/50">
                      {formatThreshold(actual, goal)}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value text-slate-600">
                      {allocatedBudget !== null && allocatedBudget > 0 ? formatCurrency(allocatedBudget) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value text-slate-600 bg-blue-50/50">
                      {actualSpend !== null && actualSpend > 0 ? formatCurrency(actualSpend) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right numeric-value bg-yellow-50/50">
                      {allocatedBudget !== null && allocatedBudget > 0 && actualSpend !== null
                        ? formatThreshold(actualSpend, allocatedBudget)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
